import React from 'react'

// core components
import GridContainer from '../../components/Grid/GridContainer.js'
import GridItem from '../../components/Grid/GridItem.js'
import Card from '../../components/Card/Card.js'
import CardHeader from '../../components/Card/CardHeader.js'
import CardIcon from '../../components/Card/CardIcon.js'
import CardBody from '../../components/Card/CardBody.js'
import UserDialog from './UserDialog.js'
import DeleteUserDialog from './DeleteUserDialog.js'
import MessageDialog from './MessageDialog.js'
import Snackbar from '@material-ui/core/Snackbar'
import Alert from '@material-ui/lab/Alert'

import SiteSelector from '../Common/SiteSelector.js'
import AtlasButtonBar from '../../components/AtlasButtonBar/AtlasButtonBar.js'

// Material UI core
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import ButtonGroup from '@material-ui/core/ButtonGroup'

// Icons
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import VisibilityIcon from '@material-ui/icons/Visibility'
import EditIcon from '@material-ui/icons/Edit'
import LanguageIcon from '@material-ui/icons/Language'
import TvIcon from '@material-ui/icons/Tv'

import { AgGridReact } from 'ag-grid-react'

// report components
import {
  dashboardResponse,
  getPortalUsers,
  getPosUsers
} from '../../layouts/Aws.js'

export default function UserManagement () {
  const defaultColumnDef = {
    flex: 5,
    sortable: true,
    suppressMovable: true,
    suppressMenu: true
  }

  const portalUserColumns = [
    {
      checkboxSelection: true,
      flex: 1
    },
    {
      field: 'username',
      flex: 8
    },
    {
      field: 'email',
      flex: 10
    },
    {
      field: 'userRoleName',
      headerName: 'Role',
      headerClass: 'text-center',
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'createdDate',
      valueFormatter: formatDate,
      headerClass: 'text-center',
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'userStatus',
      headerName: 'Status',
      headerClass: 'text-center',
      cellStyle: { textAlign: 'center' }
    }
  ]

  const posUserColumns = [
    {
      checkboxSelection: true,
      flex: 1
    },
    {
      field: 'username',
      flex: 8
    },
    {
      field: 'userRoleName',
      headerClass: 'text-center',
      cellStyle: { textAlign: 'center' }
    },
    {
      field: 'createdDate',
      valueFormatter: formatDate,
      headerClass: 'text-center',
      cellStyle: { textAlign: 'center' }
    }
  ]

  const [userColumnDefs, setUserColumnDefs] = React.useState(portalUserColumns)
  const [selectedUserType, setSelectedUserType] = React.useState('Portal')
  const [showSiteSelector, setShowSiteSelector] = React.useState(false)
  const [isLoading, setLoading] = React.useState(true)
  const [initialTabLoad, setInitialTabLoad] = React.useState(true)
  const [userDialogOpen, setUserDialogOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [siteList, setSiteList] = React.useState()
  const [userList, setUserList] = React.useState([])
  const [userRoles, setPosUserRolesList] = React.useState([])
  const [selectedSite, setSelectedSite] = React.useState(undefined)
  const [selectedSiteName, setSelectedSiteName] = React.useState(null)
  const [selectedUsers, setSelectedUsers] = React.useState([])
  const [userToEdit, setUserToEdit] = React.useState(null)
  const [tabIndex, setTabIndex] = React.useState(0)

  const [messageDialogOpen, setMessageDialogOpen] = React.useState(false)
  const [messageDialogText, setMessageDialogText] = React.useState('')
  const [inviteButtonText, setInviteButtonText] = React.useState('Invite new user')

  const [snackbarOpen, setSnackbarOpen] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState('')

  const gridRef = React.useRef(null)

  const getRowId = React.useCallback((params) => {
    return params.data.userId
  }, [])

  function formatDate (params) {
    var newDate = new Date(params.value)

    var year = newDate.getFullYear()
    var monthName = newDate.toLocaleString('default', { month: 'long' })
    var dt = newDate.getDate()

    if (dt < 10) {
      dt = '0' + dt
    }

    return (dt + ' ' + monthName + ' ' + year)
  }

  React.useEffect(() => {
    // Use the dashboard response to populate the site dropdown
    dashboardResponse().then(res => {
      setSiteList(res.sites)
    }).then(res => {
      getPortalUsers().then(res => {
        setUserList(res.data.data)
        setLoading(false)
      })
    })
  }, [])

  React.useEffect(() => {
    if (isLoading === false) {
      setInitialTabLoad(false)
    }
  }, [isLoading])

  const handleDialogOnSave = async (res, editMode) => {
    if (res.status === 409) {
      setUserDialogOpen(false)
      setDeleteDialogOpen(false)

      setMessageDialogText(res.message)
      setMessageDialogOpen(true)
    } else if (res.status === 202) {
      setUserDialogOpen(false)
      setDeleteDialogOpen(false)

      setMessageDialogText(res.message)
      setMessageDialogOpen(true)
    } else if (res.status === 200) {
      setUserDialogOpen(false)
      setDeleteDialogOpen(false)

      const message = `User ${editMode ? 'updated' : 'added'} successfully`

      setSnackbarMessage(message)
      setSnackbarOpen(true)

      // so when the grid reloads, it automatically selects the previously selected nodes, which is great in theory - it means you can
      // edit a user, and then save, and simply click edit again. However, if you do this, the dialog loads with empty details.
      // This is because we reload all of the user data upon save to keep things up to date. However, this doesn't repopulate the selectedUsers
      // value with the updated data - this only properly gets repopulated when you select another user. Due to this, an easy workaround is to
      // simply deselect all users, and allow the user to reselect a user to edit. Not ideal, but I spent a while trying to come up with better solutions
      // and hadn't thought of any I could get to work
      gridRef.current.api.deselectAll()

      switchUserType(selectedUserType) // we call this simply to trigger a reload of the data to show updated records
    }
  }

  const handleDialogClose = () => {
    setUserDialogOpen(false)
    setDeleteDialogOpen(false)
    setUserToEdit(null)
  }

  const handleDeleteDialogOnSave = (res, error) => {
    setUserDialogOpen(false)
    setDeleteDialogOpen(false)
    gridRef.current.api.deselectAll()
    setSelectedUsers([])

    if (res.status !== 200) {
      setMessageDialogText(res.message)
      setMessageDialogOpen(true)
    }

    switchUserType(selectedUserType)
  }

  const handleHideMessageDialog = () => {
    setMessageDialogOpen(false)
  }

  const onSiteSelected = (site) => {
    setLoading(true)

    siteList.forEach((item, index) => {
      if (item.id === site) {
        setSelectedSite(site)
        setSelectedSiteName(item.name)

        getPosUsers(site).then(res => {
          setUserList(res.data.posUsers)
          setPosUserRolesList(res.data.posUserRoles)
          setLoading(false)
        })
      }
    })
  }

  const switchUserType = (userType) => {
    if (userType === 'Portal') {
      setSelectedUserType('Portal')
      setShowSiteSelector(false)
      setSelectedSiteName(null)
      setUserColumnDefs(portalUserColumns)
      setInviteButtonText('Invite new user')

      setLoading(true)

      getPortalUsers().then(res => {
        setUserList(res.data.data)
        setLoading(false)
      })
    } else if (userType === 'POS') {
      setSelectedUserType('POS')
      setShowSiteSelector(true)
      setUserColumnDefs(posUserColumns)
      setInviteButtonText('Create new POS user')

      if (selectedSiteName != null) {
        setLoading(true)

        getPosUsers(selectedSite).then(res => {
          setUserList(res.data.posUsers)
          setPosUserRolesList(res.data.posUserRoles)
          setLoading(false)
        })
      }
    }
  }

  const handleTabChange = (event, tabIndex) => {
    setTabIndex(tabIndex)
    setInitialTabLoad(true)
    if (tabIndex === 0) {
      switchUserType('Portal')
    } else if (tabIndex === 1) {
      switchUserType('POS')
    }
  }

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'timeout' || reason === 'clickaway') {
      setSnackbarOpen(false)
    }
  }

  const onUserRowSelectionChanged = (event) => {
    console.log('on selection changed', event, event.api.getSelectedRows())

    setSelectedUsers(event.api.getSelectedRows())
  }

  return (
    <>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="danger" icon>
              <GridContainer>
                <GridItem sm={2} md={2} lg={2} xl={2}>
                  <CardIcon color="danger">
                    <AssignmentIndIcon />
                  </CardIcon>
                </GridItem>
                <GridItem xs={8} sm={8} md={8} lg={8} xl={8}>
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    centered
                  >
                    <Tab label="Portal Users" icon={<LanguageIcon />}/>
                    <Tab label="POS Users" icon={<TvIcon />} />
                  </Tabs>
                </GridItem>
                {
                  showSiteSelector &&
                  <>
                    <GridItem sm={2} md={2} lg={3} xl={3}></GridItem>
                    <GridItem xs={12} sm={8} md={8} lg={6} xl={6}>
                      <SiteSelector
                        siteList={siteList}
                        onSelect={onSiteSelected}
                        initialSite={selectedSite}
                        isGridItem={false}
                        autoSelect={false} />
                    </GridItem>
                    <GridItem sm={2} md={2} lg={3} xl={3}></GridItem>
                  </>
                }
              </GridContainer>
            </CardHeader>
            { selectedUserType !== 'POS' || selectedSite !== undefined
              ? <CardBody>
                <GridContainer>
                  <GridItem sm={1} md={1} lg={2} xl={2} />
                  <GridItem xs={12} sm={10} md={10} lg={8} xl={8}>
                    <AtlasButtonBar style={{ margin: '0px' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                        onClick={() => {
                          setUserDialogOpen(true)
                        }}
                        style={{ marginRight: '15px' }}
                      >
                        <AddCircleIcon />&nbsp;{inviteButtonText}
                      </Button>
                      <ButtonGroup variant="text">
                        <Button
                          color="primary"
                          disabled={selectedUsers.length !== 1}
                          onClick={() => {
                            setUserToEdit(selectedUsers[0])
                            setUserDialogOpen(true)
                          }}
                        >
                          <EditIcon />&nbsp;Edit User
                        </Button>
                        <Button
                          color="secondary"
                          disabled={selectedUsers.length === 0}
                          onClick={() => {
                            setDeleteDialogOpen(true)
                          }}
                        >
                          <VisibilityIcon />&nbsp;Delete Users {selectedUsers.length > 0 && `(${selectedUsers.length})`}
                        </Button>
                      </ButtonGroup>
                    </AtlasButtonBar>
                  </GridItem>
                  <GridItem sm={1} md={1} lg={2} xl={2} />

                  <GridItem sm={1} md={1} lg={2} xl={2} />
                  <GridItem xs={12} sm={10} md={10} lg={8} xl={8}>
                    {
                      isLoading && initialTabLoad
                        ? <LinearProgress />
                        : <div className="ag-theme-alpine" style={{ height: '700px', width: '100%' }}>
                          <AgGridReact
                            getRowId={getRowId}
                            ref={gridRef}
                            rowData={userList}
                            columnDefs={userColumnDefs}
                            defaultColDef={defaultColumnDef}
                            rowSelection="multiple"
                            rowMultiSelectWithClick={true}
                            suppressCellFocus={true}
                            onSelectionChanged={onUserRowSelectionChanged}
                            overlayNoRowsTemplate={'No users found'}
                            statusBar={{
                              statusPanels: [
                                { statusPanel: 'agTotalRowCountComponent', align: 'left' },
                                { statusPanel: 'agSelectedRowCountComponent' }
                              ]
                            }}
                          />
                        </div>
                    }
                  </GridItem>
                </GridContainer>
              </CardBody> : <div/>
            }
          </Card>
        </GridItem>
      </GridContainer>
      <UserDialog
        userType={selectedUserType}
        roles={userRoles}
        site={selectedSite}
        name={selectedSiteName}
        onClose={handleDialogClose}
        onSave={handleDialogOnSave}
        open={userDialogOpen}
        selectedUser={userToEdit}
      />
      <DeleteUserDialog
        userType={selectedUserType}
        site={selectedSite}
        open={deleteDialogOpen}
        onClose={handleDialogClose}
        onSave={handleDeleteDialogOnSave}
        selectedUsers={selectedUsers}
        selectedSite={selectedSite}
      />
      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={handleSnackbarClose}>
        <Alert variant="filled" severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <MessageDialog
        open={messageDialogOpen}
        dialogMessage={messageDialogText}
        onHide={handleHideMessageDialog}
      />
    </>
  )
}
